import { useState, useEffect } from 'react';

export const MOBILE_BREAKPOINT = 768;

/**
 * Custom hook to detect mobile devices based on screen width
 * Uses CSS media query approach for consistency with responsive design
 * @returns boolean indicating if current viewport is mobile (< 768px)
 */
export const useIsMobile = (): boolean => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    // Check if window is available (SSR safety)
    if (typeof window === 'undefined') return;

    // Check if matchMedia is available and returns a valid object
    if (!window.matchMedia) {
      console.warn('window.matchMedia is not available');
      return;
    }

    const mediaQuery = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`);
    
    // Additional safety check for test environments
    if (!mediaQuery) {
      console.warn('matchMedia returned undefined');
      return;
    }

    // Set initial value - with fallback for test environments
    setIsMobile(mediaQuery.matches ?? false);

    // Handler for media query changes
    const handleChange = (event: MediaQueryListEvent) => {
      setIsMobile(event.matches ?? false);
    };

    // Add listener for viewport changes
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
    }

    // Cleanup listener on unmount
    return () => {
      if (mediaQuery && mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleChange);
      }
    };
  }, []);

  return isMobile;
};
